/**
 * Group detail (#/group/:key) — mockup-aligned 3-column command layout.
 * All devices (#/group/_all) = sidebar card + full device list (no center tabs).
 */

/**
 * @param {object} o
 * @returns {string}
 */
function buildGroupSidebarAsideHtml(o) {
  return `
    <div class="grp-mode-b__left-col">
      <aside class="grp-mode-b__left glass-panel glass-panel--panel" aria-label="Groups">
        <div class="grp-sidebar-panel-head">
          <header class="grp-mode-b__head croc-cc-panel-head">
            <h2 class="croc-cc-panel-title">Groups</h2>
          </header>
          <label class="croc-cc-search glass-panel glass-panel--inset">
            <span class="croc-cc-search__ico" aria-hidden="true">⌕</span>
            <input type="search" id="grpSidebarSearch" placeholder="Search groups…" autocomplete="off" />
          </label>
          <div class="croc-cc-filters grp-sidebar-filters" role="group" aria-label="Device status">
            <button type="button" class="croc-cc-pill croc-cc-pill--on" data-grp-sidebar-filter="all">All</button>
            <button type="button" class="croc-cc-pill" data-grp-sidebar-filter="active">Active</button>
            <button type="button" class="croc-cc-pill" data-grp-sidebar-filter="inactive">Inactive</button>
            <button type="button" class="croc-cc-pill" data-grp-sidebar-filter="critical">Critical</button>
          </div>
          <div class="croc-cc-filters grp-sidebar-share-filters" role="group" aria-label="Share status">
            <button type="button" class="croc-cc-pill croc-cc-pill--on" data-grp-sidebar-share-filter="all">All shares</button>
            <button type="button" class="croc-cc-pill" data-grp-sidebar-share-filter="shared">Shared</button>
            <button type="button" class="croc-cc-pill" data-grp-sidebar-share-filter="unshared">Private</button>
          </div>
          <button type="button" class="btn primary btn-tap grp-sidebar-create" id="grpCreateGroupBtn">+ Create Group</button>
        </div>

        <div class="grp-sidebar-panel-body croc-cc-scroll">
          <section class="grp-sidebar-section grp-sidebar-section--groups" aria-label="Groups list">
            <h3 class="grp-sidebar-section__title">Groups</h3>
            <div class="grp-sidebar-scroll" id="grpSidebarList">${o.sidebarListHtml || ""}</div>
          </section>
        </div>

        <div class="grp-sidebar-share-actions" id="grpSidebarShareActions" hidden>
          <button type="button" class="btn sm danger btn-tap" id="grpSidebarBulkRevoke" disabled>Revoke selected shares</button>
          <p class="muted" style="margin:6px 0 0;font-size:11px;line-height:1.4">Select shared groups above, then revoke their shares.</p>
        </div>
      </aside>

      <aside class="grp-mode-b__devices glass-panel glass-panel--panel" aria-label="All boards">
        <header class="grp-mode-b__head croc-cc-panel-head grp-sidebar-devices-head">
          <h2 class="croc-cc-panel-title">All boards</h2>
        </header>
        <div class="grp-sidebar-all-devices glass-panel glass-panel--inset" id="grpSidebarAllDevices">${o.allDevicesSidebarHtml || ""}</div>
      </aside>
    </div>`;
}

/**
 * @param {object} o
 * @returns {string}
 */
function buildAllDevicesFleetCenterHtml(o) {
  const statusCls = o.statusCls || "online";
  const statusLabel = o.statusLabel || "Active";
  return `
    <div class="grp-mode-b__center-stack grp-mode-b__center-stack--all-fleet">
      <div class="grp-all-devices-fleet glass-panel glass-panel--panel">
        <header class="grp-all-devices-fleet__head">
          <div class="grp-all-devices-fleet__title-row">
            <div class="grp-mode-b__hero-icon grp-all-devices-fleet__icon" aria-hidden="true">▢</div>
            <div class="grp-all-devices-fleet__copy">
              <div class="grp-mode-b__hero-title-row">
                <h1 class="grp-mode-b__hero-title">${o.groupTitle}</h1>
                <span class="badge ${statusCls} grp-mode-b__status-badge">${statusLabel}</span>
              </div>
              <p class="muted grp-all-devices-fleet__lead">Your boards — filter, select, and apply group or zone in bulk.</p>
            </div>
          </div>
          <div class="grp-all-devices-fleet__stats">
            <span class="badge neutral">total <strong id="grpTotal">${o.total}</strong></span>
            <span class="badge online">online <strong id="grpOnline">${o.online}</strong></span>
            <span class="badge offline">offline <strong id="grpOffline">${o.offline}</strong></span>
          </div>
        </header>
        ${o.fleetTruncBanner || ""}
        <div id="grpAllDevicesBulkMount">${o.allDevicesBulkToolbarHtml || ""}</div>
        <div class="grp-mode-b__devices-scroll grp-all-devices-fleet__scroll">
          <div class="device-grid grp-device-grid device-grid--skeleton" id="groupPageDevices" aria-busy="true"></div>
          <div id="grpFleetLoadMoreWrap" style="margin-top:12px;display:none">
            <button class="btn secondary btn-tap" id="grpFleetLoadMore" type="button">Load more boards</button>
            <p class="muted" id="grpFleetLoadMoreHint" style="margin:8px 0 0"></p>
          </div>
        </div>
      </div>
    </div>`;
}

/**
 * @param {object} o
 * @returns {string}
 */
function buildGroupCommandCenterHtml(o) {
  const onlinePct = o.total > 0 ? Math.round((o.online / o.total) * 100) : 0;
  const statusCls = o.statusCls || "online";
  const statusLabel = o.statusLabel || "Active";
  return `
    <div class="grp-mode-b__center-stack">
      <header class="grp-mode-b__hero glass-panel glass-panel--hero">
        <div class="grp-mode-b__hero-main">
          <div class="grp-mode-b__hero-icon" aria-hidden="true">${o.heroInitial || "G"}</div>
          <div class="grp-mode-b__hero-copy">
            <div class="grp-mode-b__hero-title-row">
              <h1 class="grp-mode-b__hero-title">${o.groupTitle}</h1>
              <span class="badge ${statusCls} grp-mode-b__status-badge">${statusLabel}</span>
            </div>
            <div class="grp-mode-b__meta-grid">
              <div class="grp-meta-item"><span class="grp-meta-item__lbl">Group</span><strong>${o.groupTitle}</strong></div>
              <div class="grp-meta-item"><span class="grp-meta-item__lbl">Alarm link</span><strong>Linked group siren</strong></div>
              <div class="grp-meta-item"><span class="grp-meta-item__lbl">Devices</span><strong id="grpTotal">${o.total}</strong></div>
              <div class="grp-meta-item"><span class="grp-meta-item__lbl">Online</span><strong id="grpOnline" class="grp-meta-val--ok">${o.online}</strong></div>
              <div class="grp-meta-item"><span class="grp-meta-item__lbl">Offline</span><strong id="grpOffline">${o.offline}</strong></div>
              <div class="grp-meta-item"><span class="grp-meta-item__lbl">Created by</span><strong>${o.createdByLine || "—"}</strong></div>
            </div>
            ${o.shareCtxLine || ""}
            ${o.legacyFilterBanner || ""}
            ${o.fleetTruncBanner || ""}
          </div>
        </div>
        <div class="grp-mode-b__response-card glass-panel glass-panel--inset">
          <span class="grp-mode-b__section-title">Alarm link</span>
          <strong class="grp-mode-b__response-val">Linked group siren</strong>
        </div>
      </header>

      <nav class="grp-mode-b__tabs glass-panel glass-panel--inset" aria-label="Group sections">
        <button type="button" class="grp-mode-b__tab grp-mode-b__tab--active" data-grp-tab="overview" aria-selected="true">Overview</button>
        <button type="button" class="grp-mode-b__tab" data-grp-tab="devices" aria-selected="false">Devices (${o.total})</button>
        <button type="button" class="grp-mode-b__tab" data-grp-tab="members" aria-selected="false">Members</button>
        <button type="button" class="grp-mode-b__tab" data-grp-tab="rules" aria-selected="false">Rules</button>
        <button type="button" class="grp-mode-b__tab" data-grp-tab="activity" aria-selected="false">Activity</button>
        <button type="button" class="grp-mode-b__tab" data-grp-tab="map" aria-selected="false">Map</button>
      </nav>

      <div class="grp-mode-b__panels">
        <div class="grp-mode-b__panel grp-mode-b__panel--active" data-grp-panel="overview">
          <div class="grp-map-wrap glass-panel glass-panel--map">
            <div class="grp-map-toolbar glass-panel glass-panel--float">
              <span class="grp-map-toolbar__label">All Layers</span>
              <label class="grp-map-toggle"><input type="checkbox" checked disabled /> Cameras</label>
              <label class="grp-map-toggle"><input type="checkbox" checked disabled /> SOS</label>
              <label class="grp-map-toggle"><input type="checkbox" checked disabled /> Devices</label>
            </div>
            <div id="grp-map-canvas" class="grp-map-canvas" role="application" aria-label="Group GIS map"></div>
            <div class="grp-map-donut glass-panel glass-panel--float" aria-hidden="true">
              <span class="grp-map-donut__pct" id="grpMapDonutPct">${onlinePct}%</span>
              <span class="grp-map-donut__lbl">Online</span>
            </div>
          </div>
          <div class="grp-mode-b__bottom glass-panel glass-panel--panel" aria-label="Group summary">
            <section class="grp-bottom-panel">
              <h3 class="grp-mode-b__section-title">Group details</h3>
              <dl class="grp-info-list">
                <div><dt>Name</dt><dd>${o.groupTitle}</dd></div>
                <div><dt>Owner</dt><dd>${o.ownerLine}</dd></div>
              </dl>
            </section>
            <section class="grp-bottom-panel">
              <h3 class="grp-mode-b__section-title">At a glance</h3>
              <div class="grp-quick-stats">
                <div class="grp-stat-chip"><span>Total</span><strong id="grpBottomTotal">${o.total}</strong></div>
                <div class="grp-stat-chip grp-stat-chip--ok"><span>Online</span><strong id="grpBottomOnline">${o.online}</strong></div>
                <div class="grp-stat-chip grp-stat-chip--off"><span>Offline</span><strong id="grpBottomOffline">${o.offline}</strong></div>
                <div class="grp-stat-chip"><span>Rate</span><strong id="grpBottomRate">${onlinePct}%</strong></div>
              </div>
              <div class="grp-ai-panel" id="grp-ai-panel" aria-label="AI recommendation">
                <h3 class="grp-mode-b__section-title">Suggestion</h3>
                <div id="grp-ai-body" class="grp-ai-body muted">Loading…</div>
              </div>
            </section>
            <section class="grp-bottom-panel">
              <h3 class="grp-mode-b__section-title">Last Activity</h3>
              <div class="grp-activity-scroll grp-activity-scroll--bottom" id="grp-activity-list"><p class="muted">Loading…</p></div>
            </section>
          </div>
        </div>

        <div class="grp-mode-b__panel" data-grp-panel="devices" hidden>
          <div class="grp-devices-panel glass-panel glass-panel--panel">
            <h3 class="grp-mode-b__section-title">Devices in group</h3>
            <div class="grp-mode-b__devices-scroll">
              <div class="device-grid grp-device-grid" id="groupPageDevices"></div>
              <div id="grpFleetLoadMoreWrap" style="margin-top:12px;display:none">
                <button class="btn secondary btn-tap" id="grpFleetLoadMore" type="button">Load more boards</button>
                <p class="muted" id="grpFleetLoadMoreHint" style="margin:8px 0 0"></p>
              </div>
            </div>
          </div>
        </div>

        <div class="grp-mode-b__panel" data-grp-panel="members" hidden>
          <div class="grp-members-panel glass-panel glass-panel--panel" aria-label="Members">
            <h3 class="grp-mode-b__section-title">Members</h3>
            <p class="muted" style="margin:0 0 10px;font-size:12px">People shared on the group selected in the left sidebar. Manage sharing via <strong>Add Members</strong>.</p>
            <div class="grp-members-list" id="grp-members-full">${o.membersHtml || ""}</div>
          </div>
        </div>

        <div class="grp-mode-b__panel" data-grp-panel="rules" hidden>
          <div class="grp-rules-panel glass-panel glass-panel--panel" aria-label="Group rules">
            <h3 class="grp-mode-b__section-title">Group Rules</h3>
            <div id="grp-rules-full">${o.rulesHtml || ""}</div>
          </div>
        </div>

        <div class="grp-mode-b__panel" data-grp-panel="activity" hidden>
          <div class="grp-activity-panel glass-panel glass-panel--panel">
            <h3 class="grp-mode-b__section-title">Activity Log</h3>
            <div class="grp-activity-scroll grp-activity-scroll--full" id="grp-activity-full"><p class="muted">Loading…</p></div>
          </div>
        </div>

        <div class="grp-mode-b__panel" data-grp-panel="map" hidden>
          <div class="grp-map-wrap glass-panel glass-panel--map grp-map-wrap--solo">
            <div id="grp-map-canvas-solo" class="grp-map-canvas" role="application" aria-label="Group map full view"></div>
          </div>
        </div>
      </div>
    </div>

    <aside class="grp-mode-b__right glass-panel glass-panel--panel croc-cc-scroll" aria-label="Group actions">
      <h3 class="grp-mode-b__section-title">Group Actions</h3>
      <div class="grp-action-list">${o.actionButtonsHtml || ""}</div>
      <div class="divider"></div>
      <section class="grp-members-panel" aria-label="Members preview">
        <h3 class="grp-mode-b__section-title">Members (${o.memberCount != null ? o.memberCount : "—"})</h3>
        <div class="grp-members-list" id="grp-members-side">${o.membersPreviewHtml || o.membersHtml || ""}</div>
      </section>
      <div class="divider"></div>
      <section class="grp-rules-panel" aria-label="Group rules preview">
        <h3 class="grp-mode-b__section-title">Group Rules (${o.ruleCount != null ? o.ruleCount : "—"})</h3>
        <div id="grp-rules-side">${o.rulesPreviewHtml || o.rulesHtml || ""}</div>
      </section>
      ${o.dangerZoneHtml || ""}
    </aside>`;
}

/**
 * @param {object} o
 * @returns {string}
 */
function buildGroupsHubDirectoryCenterHtml(o) {
  const cardsHtml = o.hubCardsSectionHtml || "";
  return `
    <div class="grp-mode-b__center-stack grp-mode-b__center-stack--hub-directory">
      <div class="grp-hub-directory glass-panel glass-panel--panel" data-hub-empty="${cardsHtml ? "0" : "1"}">
        <header class="grp-hub-directory__head">
          <div class="grp-mode-b__hero-icon" aria-hidden="true">⎔</div>
          <div class="grp-hub-directory__copy">
            <h1 class="grp-mode-b__hero-title">${o.groupTitle || "Groups"}</h1>
            <p class="muted grp-hub-directory__lead">Manage group cards, sharing, and alarm timing — or open <a href="#/group/_all">All boards</a> · <button type="button" class="btn sm ghost btn-tap" data-cc-go="overview">Home</button>.</p>
          </div>
        </header>
        ${cardsHtml}
      </div>
    </div>`;
}

/**
 * @param {object} o
 * @returns {string}
 */
export function buildGroupDetailShellHtml(o) {
  const isAll = !!o.isAllDevicesView;
  const isHub = !!o.isHubDirectoryView;
  const gridMod = isAll ? " grp-mode-b--all-fleet" : (isHub ? " grp-mode-b--hub-directory" : "");
  const centerHtml = isAll
    ? buildAllDevicesFleetCenterHtml(o)
    : (isHub ? buildGroupsHubDirectoryCenterHtml(o) : buildGroupCommandCenterHtml(o));
  return `
<section class="grp-mode-b-page" aria-label="${isAll ? "All boards" : (isHub ? "Groups directory" : "Group command")}">
  <div class="grp-mode-b${gridMod}">
    ${buildGroupSidebarAsideHtml(o)}
    ${centerHtml}
  </div>
</section>
${o.hubModalsHtml || ""}
<div class="modal-overlay" id="createGroupModal" hidden aria-hidden="true">
  <div class="modal" role="dialog" aria-modal="true" aria-labelledby="createGroupModalTitle">
    <div class="modal-head">
      <h3 id="createGroupModalTitle">Create group</h3>
      <button type="button" class="icon-btn" id="createGroupModalClose" aria-label="Close">✕</button>
    </div>
    <div class="modal-body">
      <label class="field">
        <span>Group name</span>
        <input type="text" id="createGroupName" placeholder="e.g. Warehouse A" autocomplete="off" maxlength="80" />
      </label>
      <div class="create-group-dev-list" id="createGroupDevList">
        <p class="muted">Loading devices…</p>
      </div>
    </div>
    <div class="modal-foot">
      <button type="button" class="btn secondary" id="createGroupModalCancel">Cancel</button>
      <button type="button" class="btn primary" id="createGroupModalSubmit" disabled>Create group</button>
    </div>
  </div>
</div>`;
}
